/**
 * GLAMRDiP shared scroll lock — iOS-safe.
 *
 * Joel 2026-04-30. The naive `body { overflow: hidden }` pattern that
 * was repeated across 8+ modal modules in this codebase doesn't fully
 * lock body scroll on iOS Safari — touches that start inside a modal
 * scroll container at its scroll boundary chain to the page beneath.
 * Result: bundle builder modal lets the PDP scroll behind it.
 *
 * Pattern used here:
 *   1. Save current scrollY before locking.
 *   2. Apply position: fixed; top: -savedY; width: 100% to the body.
 *      This visually pins the page exactly where the user was AND
 *      makes the body unscrollable (you can't scroll a fixed body).
 *   3. On unlock: clear the styles AND scrollTo the saved Y so the
 *      user lands back at their original scroll position.
 *
 * Reference-counted so multiple stacked modals (e.g. upsell modal
 * mounting inside an already-locked bundle modal) don't stomp each
 * other's lock state. Only the FIRST lock saves scrollY and applies
 * the fixed position; subsequent locks just bump the count. Only the
 * LAST unlock restores. Belt-and-braces against future drift.
 *
 * Public API:
 *   window.gdScrollLock.lock(scrollableEl?)
 *      - locks body scroll; saves scroll position
 *      - if scrollableEl is passed, applies overscroll-behavior: contain
 *        to it so the scroll-chain bug (gestures escaping the modal)
 *        is also defended at the modal-scroll-container level
 *   window.gdScrollLock.unlock()
 *      - decrements counter; restores body when zero
 *   window.gdScrollLock.isLocked()
 *      - boolean for callers that want to check state
 *   window.gdScrollLock.getDebugLog()
 *      - returns the in-memory ring buffer of recent lock/unlock events
 *        when ?debug=scroll is in the URL. Use from Safari Web Inspector
 *        on a real iPhone to dump the trace after reproducing a bug.
 *
 * INSTRUMENTATION (Joel 2026-05-02 — round N debug):
 * Gate via `?debug=scroll` URL param. When enabled:
 *   - Every lock/unlock logs to console with caller identifier, lockCount
 *     before/after, scrollY at call time, savedScrollY before/after, body
 *     style snapshot before/after, performance.now() timestamp, paired
 *     callId (so lock/unlock from same caller can be correlated).
 *   - In-memory ring buffer of the last 200 events for post-hoc review
 *     via window.gdScrollLock.getDebugLog().
 *
 * To reproduce a scroll-lock bug on iOS Safari:
 *   1. Visit the staging site with `?debug=scroll` appended to the URL.
 *   2. Connect Safari Web Inspector from a Mac to your iPhone.
 *   3. Open Console tab.
 *   4. Reproduce the bug (open + close cart drawer / mobile menu / reels).
 *   5. Run `copy(JSON.stringify(window.gdScrollLock.getDebugLog(), null, 2))`
 *      in the console — copies the trace to clipboard.
 *   6. Paste into the Phase 3 report.
 */
(() => {
  'use strict';

  let lockCount = 0;
  let savedScrollY = 0;
  const containedEls = new Set();

  // ──────── DEBUG INSTRUMENTATION ────────
  const DEBUG = (() => {
    try {
      return new URLSearchParams(window.location.search).get('debug') === 'scroll';
    } catch (e) {
      return false;
    }
  })();
  const _debugLog = [];
  let _callIdCounter = 0;
  function describeEl(el) {
    if (!el) return 'null';
    if (!(el instanceof Element)) return String(el);
    const id = el.id ? `#${el.id}` : '';
    const cls = el.className && typeof el.className === 'string'
      ? '.' + el.className.split(/\s+/).slice(0, 3).join('.')
      : '';
    const data = (() => {
      const dKeys = Object.keys(el.dataset || {}).filter((k) => k.startsWith('gd'));
      return dKeys.length ? `[${dKeys.map((k) => 'data-' + k.replace(/[A-Z]/g, (c) => '-' + c.toLowerCase())).join(',')}]` : '';
    })();
    return `<${el.tagName.toLowerCase()}${id}${cls}${data}>`;
  }
  function snapshotBody() {
    const s = document.body.style;
    return {
      position: s.position || '',
      top: s.top || '',
      width: s.width || '',
      overflow: s.overflow || '',
      overscrollBehavior: s.overscrollBehavior || '',
    };
  }
  function logEvent(kind, caller, before, after) {
    if (!DEBUG) return;
    const entry = {
      t: Math.round(performance.now()),
      kind,
      callId: before.callId,
      caller: describeEl(caller),
      lockCount: { before: before.lockCount, after: after.lockCount },
      scrollY: before.scrollY,
      savedScrollY: { before: before.savedScrollY, after: after.savedScrollY },
      body: { before: before.body, after: after.body },
      stack: (new Error().stack || '').split('\n').slice(2, 5).map((s) => s.trim()),
    };
    _debugLog.push(entry);
    if (_debugLog.length > 200) _debugLog.shift();
    // eslint-disable-next-line no-console
    console.log(`[gd-scroll-lock] ${kind} #${entry.callId} ${describeEl(caller)} count ${before.lockCount}→${after.lockCount} scrollY=${before.scrollY} saved=${before.savedScrollY}→${after.savedScrollY}`, entry);
  }

  function applyBodyLock() {
    savedScrollY = window.scrollY || window.pageYOffset || 0;
    const body = document.body;
    body.style.position = 'fixed';
    body.style.top = `-${savedScrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    // Belt-and-braces — older browsers + iOS edge cases honor this even
    // if position:fixed didn't fully bind.
    body.style.overflow = 'hidden';
    // overscroll-behavior on body for browsers that support it (most
    // modern; iOS 16+). Stops scroll-chaining from any descendant.
    body.style.overscrollBehavior = 'contain';
  }

  function releaseBodyLock() {
    // Joel 2026-05-02 (round N+1 fix without trace): two layered fixes
    // for the recurring iOS scroll-jump on overlay close.
    //
    // 1. CAPTURE savedScrollY into a closure-local BEFORE clearing it.
    //    The previous `window.scrollTo(0, savedScrollY); savedScrollY = 0`
    //    sequence was synchronous and worked, but if the scrollTo is
    //    deferred (see #2), the module-level savedScrollY would be 0 by
    //    the time the deferred call fires, scrolling user to top.
    //
    // 2. DEFER scrollTo via requestAnimationFrame after clearing
    //    `position: fixed` from body. iOS Safari's layout engine doesn't
    //    re-flow the body synchronously when the style property is
    //    cleared — there's a frame of deferred layout. Calling scrollTo
    //    inside that window is sometimes ignored or lands at the wrong
    //    position. rAF guarantees we restore AFTER the layout has
    //    re-applied, so the document's scroll context is real.
    //    `behavior: 'instant'` overrides any global `scroll-behavior:
    //    smooth` CSS rule that would otherwise animate the restore.
    //    Force-layout via offsetHeight is belt-and-braces against
    //    rAF-scheduling quirks on older Safari.
    const restoreY = savedScrollY;
    savedScrollY = 0;
    const body = document.body;
    body.style.position = '';
    body.style.top = '';
    body.style.left = '';
    body.style.right = '';
    body.style.width = '';
    body.style.overflow = '';
    body.style.overscrollBehavior = '';
    // Force layout flush so rAF callback observes the un-fixed body.
    void body.offsetHeight;
    requestAnimationFrame(() => {
      try {
        window.scrollTo({ top: restoreY, left: 0, behavior: 'instant' });
      } catch (e) {
        // Older Safari without `behavior: 'instant'` — fall back to
        // the 2-arg form, which is always synchronous + non-animated.
        window.scrollTo(0, restoreY);
      }
    });
  }

  function lock(scrollableEl) {
    const before = {
      callId: ++_callIdCounter,
      lockCount,
      savedScrollY,
      scrollY: window.scrollY || window.pageYOffset || 0,
      body: DEBUG ? snapshotBody() : null,
    };
    if (lockCount === 0) applyBodyLock();
    lockCount++;
    if (scrollableEl && !containedEls.has(scrollableEl)) {
      // Apply overscroll-behavior on the modal's scroll container so
      // scroll gestures that hit its boundaries don't chain to body.
      // Saves prior value so unlock() can restore (rare — most modals
      // don't have an explicit overscroll setting).
      scrollableEl.dataset.gdScrollLockPrior = scrollableEl.style.overscrollBehaviorY || '';
      scrollableEl.style.overscrollBehaviorY = 'contain';
      containedEls.add(scrollableEl);
    }
    logEvent('LOCK', scrollableEl, before, {
      lockCount,
      savedScrollY,
      body: DEBUG ? snapshotBody() : null,
    });
  }

  function unlock(scrollableEl) {
    const before = {
      callId: ++_callIdCounter,
      lockCount,
      savedScrollY,
      scrollY: window.scrollY || window.pageYOffset || 0,
      body: DEBUG ? snapshotBody() : null,
    };
    // Joel 2026-05-02 (round N+1 fix without trace): double-unlock guard.
    // When unlock is called and lockCount is already 0, the previous
    // `Math.max(0, lockCount - 1)` clamped to 0 + the trailing
    // `if (lockCount === 0) releaseBodyLock()` would re-run releaseBodyLock
    // with savedScrollY already 0, scrolling page to TOP. This pattern
    // can fire when:
    //   - A modal's close path runs twice (e.g. ESC + click-outside +
    //     X button race)
    //   - A scrollable element is passed for unlock that was never
    //     locked (caller bug)
    //   - A modal close fires after a different modal's lock was
    //     released by beforeunload cleanup
    // Early-return when count is already 0 — true no-op for repeated
    // unlocks.
    if (lockCount === 0) {
      logEvent('UNLOCK_NOOP', scrollableEl, before, {
        lockCount,
        savedScrollY,
        body: DEBUG ? snapshotBody() : null,
      });
      return;
    }
    lockCount = Math.max(0, lockCount - 1);
    if (scrollableEl && containedEls.has(scrollableEl)) {
      scrollableEl.style.overscrollBehaviorY = scrollableEl.dataset.gdScrollLockPrior || '';
      delete scrollableEl.dataset.gdScrollLockPrior;
      containedEls.delete(scrollableEl);
    }
    if (lockCount === 0) releaseBodyLock();
    logEvent('UNLOCK', scrollableEl, before, {
      lockCount,
      savedScrollY,
      body: DEBUG ? snapshotBody() : null,
    });
  }

  function isLocked() { return lockCount > 0; }

  function getDebugLog() { return _debugLog.slice(); }

  function clearDebugLog() { _debugLog.length = 0; }

  window.gdScrollLock = { lock, unlock, isLocked, getDebugLog, clearDebugLog };

  if (DEBUG) {
    // eslint-disable-next-line no-console
    console.log('[gd-scroll-lock] DEBUG MODE ON. Run window.gdScrollLock.getDebugLog() to dump trace. URL param: ?debug=scroll');
  }

  // Joel 2026-05-01: cleanup on page unload. Prevents fixed-positioned
  // body persisting across a page refresh if a modal was open at
  // refresh time — without this, the new page load inherits a body
  // with position:fixed + top:-1500px which clamps the next scroll
  // position to weird values. beforeunload runs synchronously before
  // the page unloads, so the cleanup is reliable.
  window.addEventListener('beforeunload', () => {
    const body = document.body;
    body.style.position = '';
    body.style.top = '';
    body.style.left = '';
    body.style.right = '';
    body.style.width = '';
    body.style.overflow = '';
    body.style.overscrollBehavior = '';
  });

  // Joel 2026-05-01: tell the browser to restore scroll position
  // natively on refresh + back/forward navigation. Custom restoration
  // (e.g. via JS reading sessionStorage) almost always conflicts with
  // modal scroll-lock state in subtle ways. Auto = browser does the
  // right thing without help.
  if ('scrollRestoration' in history) {
    history.scrollRestoration = 'auto';
  }
})();
